import React from 'react';
import clsx from 'clsx';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import PCCStatusPill from 'pages/Dashboard/components/PCCStatusPill';
import {
  PCCProgramMeta,
  PCCProgramStatus,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/utils';

const PROGRAM_ROW_BASE_CLASS = 'flex min-h-12 gap-2.5 py-2 text-xs';
const PROGRAM_ICON_CLASS = '!h-[18px] !w-[18px] !shrink-0 !text-inherit';
const PROGRAM_TEXT_CLASS = 'flex min-w-0 flex-1 flex-col gap-0.5';

type StatusRowProps = {
  program: PCCProgramStatus;
  className?: string;
};

export function PCCPopoverStatusRow({
  program,
  className,
}: StatusRowProps) {
  const ineligibilityReasons = program.ineligibilityReasons ?? [];
  const isNotEligible = program.state === 'notEligible';
  const showIneligibilityReasons = isNotEligible && ineligibilityReasons.length > 0;

  return (
    <div
      className={clsx(
        PROGRAM_ROW_BASE_CLASS,
        {
          'items-start text-gray-400': showIneligibilityReasons,
          'items-center': !showIneligibilityReasons,
          'text-gray-400': isNotEligible,
          'text-gray-600': !isNotEligible,
        },
        className,
      )}
    >
      <program.Icon className={PROGRAM_ICON_CLASS} />
      <div className={PROGRAM_TEXT_CLASS}>
        <span
          className={clsx(
            'truncate font-semibold',
            {
              'text-gray-700': isNotEligible,
              'text-gray-900': !isNotEligible,
            },
          )}
        >
          {program.label}
        </span>
        <span className="truncate text-xs font-normal text-gray-500">
          {program.description}
        </span>
        {showIneligibilityReasons ? (
          <ul className="m-0 mt-1 list-disc pl-4 text-xs font-medium text-gray-600">
            {ineligibilityReasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        ) : null}
      </div>
      <PCCStatusPill
        state={program.state}
        className="shrink-0"
      />
    </div>
  );
}

type PCCProgramSelectionRowProps = {
  program: PCCProgramMeta;
  canEnroll: boolean;
  isEnrolling: boolean;
  isSelected: boolean;
  checkboxId: string;
  className?: string;
  onToggle: (checked: boolean) => void;
};

export function PCCProgramSelectionRow({
  program,
  canEnroll,
  isEnrolling,
  isSelected,
  checkboxId,
  className,
  onToggle,
}: PCCProgramSelectionRowProps) {
  return (
    <label
      htmlFor={checkboxId}
      className={clsx(
        PROGRAM_ROW_BASE_CLASS,
        'cursor-pointer items-center rounded-md px-2.5 text-gray-600 hover:bg-blue-100',
        {
          'bg-blue-50': isSelected,
        },
        className,
      )}
    >
      <program.Icon className={PROGRAM_ICON_CLASS} />
      <div className={PROGRAM_TEXT_CLASS}>
        <span className="truncate font-semibold text-gray-900">
          {program.label}
        </span>
        <span className="truncate text-xs font-normal text-gray-500">
          {program.description}
        </span>
      </div>
      <Checkbox
        id={checkboxId}
        color="primary"
        className="!h-[18px] !w-[18px] !p-0 [&.Mui-checked]:!text-blue-500 [&_.MuiSvgIcon-root]:!h-[18px] [&_.MuiSvgIcon-root]:!w-[18px]"
        containerClassName="shrink-0"
        checked={isSelected}
        disabled={!canEnroll || isEnrolling}
        onChange={(checked) => { onToggle(checked); }}
      />
    </label>
  );
}
